import { EmailBanner } from './auth/EmailBanner.tsx';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { GameSummary } from '@replay/shared';
import { api, ApiError } from '../lib/api.ts';
import { useLocalGames } from '../lib/offline.ts';
import { useAuth, useMe } from '../lib/auth.tsx';
import { ago, systemAccent, systemName } from '../lib/format.ts';
import { useHints } from '../lib/nav/react.tsx';
import { ambientFor, useAmbient } from '../components/Ambient.tsx';
import { Cover, Shelf } from '../components/GameCard.tsx';
import { Icon } from '../components/Icon.tsx';
import { ErrorState, Loading } from '../components/ui.tsx';

interface HomeData {
  featured: GameSummary[];
  continuePlaying: (GameSummary & { lastPlayedAt: string })[];
  favorites: GameSummary[];
  newest: GameSummary[];
  hacks: GameSummary[];
  multiplayer: GameSummary[];
  collections: { id: string; slug: string; name: string; description: string; games: GameSummary[] }[];
  friendsPlaying: { userId: string; displayName: string; gameId: string; title: string }[];
  motd: string;
}

/** Stable daily shuffle so "Descubre algo nuevo" changes each day, not on every render. */
function dailyPick<T extends { id: string }>(items: T[], n: number) {
  const day = new Date().toISOString().slice(0, 10);
  const score = (id: string) => {
    let h = 2166136261;
    for (const c of day + id) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
    return h >>> 0;
  };
  return [...items].sort((a, b) => score(a.id) - score(b.id)).slice(0, n);
}

function Hero({ game, lastPlayedAt }: { game: GameSummary; lastPlayedAt?: string }) {
  return (
    <section className={`hero${game.heroUrl ? ' has-art' : ''}`} aria-label={lastPlayedAt ? 'Continuar jugando' : 'Destacado'}>
      {game.heroUrl && <div className="hero-art" style={{ backgroundImage: `url("${game.heroUrl}")` }} aria-hidden="true" />}
      <div className="hero-copy">
        <span className="hero-kicker">
          {lastPlayedAt ? (
            <>
              <span className="live" aria-hidden="true" /> Continuar jugando · {ago(lastPlayedAt)}
            </>
          ) : (
            'Destacado en tu biblioteca'
          )}
        </span>
        <h1>{game.title}</h1>
        <div className="hero-meta">
          <span>
            <b>{systemName(game.systemId)}</b>
          </span>
          {game.year && <span>{game.year}</span>}
          {game.genres.length > 0 && <span>{game.genres.slice(0, 2).join(' · ')}</span>}
          {game.players && game.players > 1 && <span>Hasta {game.players} jugadores</span>}
          {game.isHack && <span>Hackrom</span>}
        </div>
        <div className="hero-actions">
          <Link to={`/play/${game.id}`} className="btn btn-primary btn-lg" data-autofocus>
            <Icon name="play" /> {lastPlayedAt ? 'Continuar' : 'Jugar'}
          </Link>
          <Link to={`/game/${game.slug}`} className="btn btn-ghost btn-lg">
            Ver ficha
          </Link>
        </div>
      </div>
      {!game.heroUrl && (
        <div className="hero-cover" aria-hidden="true">
          <div className="box">
            <Cover game={game} eager />
          </div>
        </div>
      )}
    </section>
  );
}

function Rail({ title, games, to, subtitle }: { title: string; games: GameSummary[]; to?: string; subtitle?: (g: GameSummary) => string | undefined }) {
  if (!games.length) return null;
  return (
    <section className="rail-section" aria-label={title}>
      <div className="rail-head">
        <h2>{title}</h2>
        <span className="count">{games.length}</span>
        {to && <Link to={to}>Ver todo</Link>}
      </div>
      <Shelf games={games} subtitle={subtitle} label={title} />
    </section>
  );
}

/** Without a connection: the games on this device, ready to play. */
function OfflineHome() {
  const local = useLocalGames();
  const games = local.data ?? [];
  const hero = games[0];
  useAmbient(hero ? ambientFor(hero, systemAccent) : null);
  if (local.isLoading) return <Loading />;
  return (
    <div className="home">
      <EmailBanner />
      <section className="hero" aria-label="Sin conexión">
        <div className="hero-copy">
          <span className="hero-kicker">
            <Icon name="cloudOff" size={16} /> Sin conexión · modo sin conexión
          </span>
          <h1>{hero ? hero.title : 'Sin conexión'}</h1>
          <p className="muted prose">
            {hero
              ? 'Puedes jugar a los juegos descargados en este dispositivo. Tus partidas se guardan aquí y se subirán solas cuando vuelva Internet.'
              : 'No hay juegos descargados en este dispositivo. Cuando vuelva la conexión, usa «Descargar para jugar sin conexión» en la ficha de tus juegos.'}
          </p>
          {hero && (
            <div className="hero-actions">
              <Link to={`/play/${hero.id}`} className="btn btn-primary btn-lg" data-autofocus>
                <Icon name="play" /> Jugar
              </Link>
            </div>
          )}
        </div>
        {hero && (
          <div className="hero-cover" aria-hidden="true">
            <div className="box">
              <Cover game={hero} eager />
            </div>
          </div>
        )}
      </section>
      {games.length > 1 && <Rail title="En este dispositivo" games={games.slice(1)} />}
    </div>
  );
}

export function Home() {
  const me = useMe();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['home'], queryFn: () => api<HomeData>('/api/home') });
  const d = q.data;
  const last = d?.continuePlaying[0];
  const hero = last ?? d?.featured[0] ?? d?.newest[0];
  useAmbient(hero ? ambientFor(hero, systemAccent) : null);
  useHints([
    { button: 'Y', label: 'Favorito' },
    { button: 'Start', label: 'Menú' },
  ]);

  const discover = useMemo(() => {
    if (!d) return [];
    const played = new Set(d.continuePlaying.map((g) => g.id));
    return dailyPick(
      d.newest.filter((g) => !played.has(g.id)),
      12,
    );
  }, [d]);

  if (q.isLoading) return <Loading />;
  if (q.error instanceof ApiError && q.error.kind === 'network') return <OfflineHome />;
  if (q.error || !d) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;

  if (!hero) {
    return (
      <>
      <EmailBanner />
      <section className="hero" aria-label="Bienvenida">
        <div className="hero-copy">
          <span className="hero-kicker">Hola, {me.displayName}</span>
          <h1>Tu biblioteca está lista para llenarse</h1>
          <p className="muted prose">
            {can('games.upload')
              ? 'Sube ROMs, hackroms o juegos de PC desde Administración y aparecerán aquí con su carátula.'
              : 'Todavía no hay juegos. Pide los que te gustaría jugar y la administración los añadirá.'}
          </p>
          <div className="hero-actions">
            {can('games.upload') ? (
              <Link to="/admin/import" className="btn btn-primary btn-lg" data-autofocus>
                <Icon name="upload" /> Añadir juegos
              </Link>
            ) : (
              <Link to="/requests" className="btn btn-primary btn-lg" data-autofocus>
                <Icon name="inbox" /> Pedir un juego
              </Link>
            )}
          </div>
        </div>
      </section></>
    );
  }

  return (
    <div className="home">
      <EmailBanner />
      <Hero game={hero} lastPlayedAt={last?.lastPlayedAt} />
      {d.motd && (
        <p className="notice" style={{ margin: '0 var(--gutter)' }}>
          {d.motd}
        </p>
      )}

      {d.friendsPlaying.length > 0 && (
        <section className="rail-section" aria-label="Amigos jugando">
          <div className="rail-head">
            <h2>Tus amigos están jugando</h2>
          </div>
          <div className="rail" data-nav-row style={{ gridAutoColumns: 'max-content' }}>
            {d.friendsPlaying.map((f) => (
              <Link key={f.userId} to={`/game/${f.gameId}`} className="chip">
                <span className="dot on" /> {f.displayName} · {f.title}
              </Link>
            ))}
          </div>
        </section>
      )}

      <Rail
        title="Continuar jugando"
        games={d.continuePlaying.slice(1)}
        subtitle={(g) => `Jugado ${ago((g as HomeData['continuePlaying'][number]).lastPlayedAt)}`}
      />
      <Rail title="Tus favoritos" games={d.favorites} to="/library?favorites=1" />
      {d.collections.map((c) => (
        <Rail key={c.id} title={c.name} games={c.games} to={`/collections/${c.slug}`} />
      ))}
      <Rail title="Recién añadidos" games={d.newest} to="/library?sort=recent" />
      <Rail title="Hackroms" games={d.hacks} to="/library?hacks=1" />
      <Rail title="Para jugar con alguien al lado" games={d.multiplayer} to="/library?players=2" />
      {discover.length > 3 && <Rail title="Descubre algo nuevo" games={discover} to="/library" />}
      <div style={{ height: 'var(--s-8)' }} />
    </div>
  );
}
